import { useRef, useState, useEffect } from 'react';
import { addToast } from '@heroui/react';
import {
  FiMessageSquare,
  FiPaperclip,
  FiSend,
  FiFile,
  FiLock,
} from 'react-icons/fi';
import ModernCard from './ModernCard';
import CustomButton from '../../CustomButton';
import CustomTextArea from './CustomTextArea';
import {
  useFetchJobRevisions,
  useFetchJobThread,
} from '../../../../hook/useQueryData';
import {
  usePostThreadMessage,
  useUploadThreadAttachment,
} from '../../../../hook/useMutationData';
import {
  QuoteThreadAttachment,
  QuoteThreadMessage,
} from '../../../../utils/type';
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/jobDetail.helpers';

// UX-only mirror of the server's multer limits
// (`quoteThreadAttachmentUpload.js`) — the server re-validates every file
// (MIME allowlist + PDF magic-byte check) regardless of what's enforced here.
const MAX_FILES = 5;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
];

const isImageAttachment = (mimeType: string) => mimeType.startsWith('image/');

const ThreadMessageRow = ({ message }: { message: QuoteThreadMessage }) => {
  const isCustomer = message.authorRole === 'customer';
  return (
    <div
      className={`flex flex-col ${isCustomer ? 'items-end' : 'items-start'}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-xs ${
          isCustomer
            ? 'bg-primary text-white rounded-br-sm'
            : 'bg-slate-100 text-slate-800 rounded-bl-sm'
        }`}
      >
        {message.body && <p className="whitespace-pre-line">{message.body}</p>}
        {message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message.attachments.map((att, i) =>
              isImageAttachment(att.mimeType) ? (
                <a
                  key={`${att.url}-${i}`}
                  href={att.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-16 h-16 rounded-lg overflow-hidden border border-white/30"
                >
                  <img
                    src={att.url}
                    alt={att.fileName}
                    className="w-full h-full object-cover"
                  />
                </a>
              ) : (
                <a
                  key={`${att.url}-${i}`}
                  href={att.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium ${
                    isCustomer ? 'bg-white/15' : 'bg-white'
                  }`}
                >
                  <FiFile size={14} />
                  <span className="truncate max-w-[120px]">{att.fileName}</span>
                </a>
              )
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-1 px-1">
        {isCustomer ? 'You' : 'Provider'} · {formatDate(message.createdAt)}
      </p>
    </div>
  );
};

/**
 * Negotiation thread panel (customer portal). Reads
 * `GET /job/:id/thread` + `GET /job/:id/revisions` (PR #869,
 * gigmosaic-be-product-mgmt-service). Renders under `RequestSummaryCard` in
 * `JobDetail.tsx`'s standard (non-batch) layout — never shown for broadcast
 * jobs, which have negotiation disabled server-side.
 *
 * The composer's visibility is driven entirely by `thread.locked` from the
 * API — this component never re-derives negotiability from `job.status`.
 *
 * `bare` (default false) controls presentation only: when true, the thread
 * renders as a bordered section (no own card chrome) so a host can nest it
 * inside another `ModernCard` — used by the single-quote layout in
 * `JobDetail.tsx` to merge it into the "Provider's Quote" card. When false
 * (default), it renders in its own `ModernCard`, matching the historical
 * standalone-card layout — used for the batch/broadcast call site, which has
 * no host card to merge into.
 */
const QuoteNegotiationThread = ({
  jobId,
  currentRevisionNo,
  hasRequestedChanges,
  revealSignal,
  bare = false,
}: {
  jobId: string;
  currentRevisionNo?: number;
  hasRequestedChanges?: boolean;
  revealSignal?: number;
  bare?: boolean;
}) => {
  const { data: thread, isLoading, isError } = useFetchJobThread(jobId);
  const { data: revisions } = useFetchJobRevisions(jobId);
  const uploadAttachment = useUploadThreadAttachment();
  const postMessage = usePostThreadMessage();

  const [draftText, setDraftText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showAllRevisions, setShowAllRevisions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isSending = uploadAttachment.isPending || postMessage.isPending;

  // Scroll into view and focus textarea when revealSignal changes
  useEffect(() => {
    if (revealSignal && revealSignal > 0 && !isError) {
      requestAnimationFrame(() => {
        const threadEl = document.getElementById('quote-negotiation-thread');
        if (threadEl) {
          threadEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          textareaRef.current?.focus();
        }
      });
    }
  }, [revealSignal, isError]);

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList) return;
    const files = Array.from(fileList);
    const validFiles: File[] = [];

    for (const file of files) {
      if (pendingFiles.length + validFiles.length >= MAX_FILES) {
        addToast({
          title: 'Too many files',
          description: `You can attach up to ${MAX_FILES} files per message.`,
          color: 'warning',
        });
        break;
      }
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        addToast({
          title: 'Unsupported file type',
          description: `${file.name} must be an image or PDF.`,
          color: 'warning',
        });
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        addToast({
          title: 'File too large',
          description: `${file.name} exceeds the 5MB limit.`,
          color: 'warning',
        });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Visibility gate: render nothing if conditions not met, except on error (fail closed)
  const hasRevisionsBeyondFirst = (revisions?.length ?? 0) > 1;
  if (
    !isError &&
    !thread?.messages?.length &&
    !hasRevisionsBeyondFirst &&
    !hasRequestedChanges
  ) {
    return null;
  }

  const handleSend = async () => {
    const trimmed = draftText.trim();
    if (!trimmed && pendingFiles.length === 0) return;

    try {
      let uploadedAttachments: QuoteThreadAttachment[] = [];
      if (pendingFiles.length > 0) {
        uploadedAttachments = await uploadAttachment.mutateAsync({
          jobId,
          files: pendingFiles,
        });
      }

      await postMessage.mutateAsync({
        jobId,
        body: trimmed || undefined,
        attachments: uploadedAttachments,
      });

      setDraftText('');
      setPendingFiles([]);
    } catch {
      // Errors are already surfaced via the mutations' onError toasts.
    }
  };

  const visibleRevisions =
    !showAllRevisions && (revisions?.length ?? 0) > 3
      ? revisions?.slice(0, 3)
      : revisions;
  const hiddenRevisionCount = revisions
    ? revisions.length - (visibleRevisions?.length ?? 0)
    : 0;

  const body = (
    <>
      {isLoading ? (
        <p className="text-xs text-slate-400">Loading thread…</p>
      ) : isError || !thread ? (
        // Fail closed on a fetch error — never show a composer for a
        // thread we couldn't confirm is unlocked.
        <p className="text-xs text-danger">
          Couldn&apos;t load the negotiation thread. Refresh the page to try
          again.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 max-h-96 overflow-y-auto pr-1">
            {thread?.messages.length ? (
              thread.messages.map((message) => (
                <ThreadMessageRow key={message._id} message={message} />
              ))
            ) : (
              <p className="text-xs text-slate-400">
                No messages yet. Ask a question or request changes to this quote
                below.
              </p>
            )}
          </div>

          {thread?.locked ? (
            <div className="flex text-xs items-center gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-xs font-medium">
              <FiLock size={14} className="shrink-0" />
              <span>
                This thread is locked
                {thread.lockedReason ? ` (${thread.lockedReason})` : ''} — no
                further messages can be sent.
              </span>
            </div>
          ) : (
            <div className="space-y-2 pt-3 border-t border-slate-100">
              <CustomTextArea
                ref={textareaRef}
                label=""
                placeholder="Type a message…"
                value={draftText}
                onValueChange={setDraftText}
                minRows={2}
                maxRows={5}
                maxLength={2000}
                isDisabled={isSending}
              />
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {pendingFiles.map((file, i) => (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 text-xs"
                    >
                      <span className="truncate text-xs max-w-[140px]">
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePendingFile(i)}
                        className="text-slate-400 hover:text-danger font-bold"
                        aria-label={`Remove ${file.name}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_MIME_TYPES.join(',')}
                  className="hidden"
                  onChange={(e) => {
                    handleFilesSelected(e.target.files);
                    e.target.value = '';
                  }}
                />
                <CustomButton
                  label="Attach"
                  variant="flat"
                  color="default"
                  size="sm"
                  className="text-xs"
                  startContent={<FiPaperclip size={12} />}
                  onPress={() => fileInputRef.current?.click()}
                  isDisabled={isSending || pendingFiles.length >= MAX_FILES}
                />
                <CustomButton
                  label="Send"
                  color="primary"
                  size="sm"
                  className="text-xs"
                  startContent={<FiSend size={12} />}
                  onPress={handleSend}
                  isLoading={isSending}
                  isDisabled={
                    isSending ||
                    (!draftText.trim() && pendingFiles.length === 0)
                  }
                />
              </div>
            </div>
          )}

          {revisions && revisions.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                Revision History
              </p>
              <div className="space-y-1.5">
                {visibleRevisions?.map((revision) => {
                  const isCurrent = revision.revisionNo === currentRevisionNo;
                  return (
                    <div
                      key={revision._id}
                      className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs ${
                        isCurrent
                          ? 'bg-primary/10 text-xs text-primary font-semibold'
                          : 'text-slate-500 text-xs'
                      }`}
                    >
                      <span>
                        Revision {revision.revisionNo}
                        {revision.revisionType === 'revised'
                          ? ' (revised)'
                          : ''}
                        {isCurrent ? ' · Current' : ''}
                      </span>
                      {typeof revision.totalPrice === 'number' && (
                        <span>{formatCurrency(revision.totalPrice)}</span>
                      )}
                    </div>
                  );
                })}
                {hiddenRevisionCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllRevisions(true)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    +{hiddenRevisionCount} more
                  </button>
                )}
                {showAllRevisions && (revisions?.length ?? 0) > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllRevisions(false)}
                    className="text-xs font-medium text-slate-400 hover:underline"
                  >
                    Show less
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div id="quote-negotiation-thread">
      {bare ? (
        <div className="pt-5 mt-5 border-t border-slate-200/60">
          <div className="flex items-center gap-1.5 mb-3">
            <FiMessageSquare size={14} className="text-primary shrink-0" />
            <h4 className="text-sm font-semibold text-slate-700">
              Negotiation Thread
            </h4>
          </div>
          <div className="flex flex-col gap-3">{body}</div>
        </div>
      ) : (
        <ModernCard
          className="mt-6"
          title={
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
                <FiMessageSquare size={16} />
              </div>
              <span className="font-semibold text-sm text-slate-800">
                Negotiation Thread
              </span>
            </div>
          }
        >
          <div className="flex flex-col gap-4">{body}</div>
        </ModernCard>
      )}
    </div>
  );
};

export default QuoteNegotiationThread;
